import { useStacklineTheme } from '@stackline/ui';
import React, { useMemo } from 'react';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { useBaseMetricRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';

import LegendWithMultipleElement from 'src/components/BeaconV2Components/Legend/LegendWithMultipleElement';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import StackedTopEntityChart, {
  XAxisLabel
} from 'src/components/BeaconV2Components/TopEntityChartV2/StackedTopEntityChart';
import { useAppSelector, useMainTimePeriod, useMainTimePeriodShortDisplayName } from 'src/utils/Hooks';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';
import _capitalize from 'lodash/capitalize';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { saveStandardStackedBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';
import ReactDOMServer from 'react-dom/server';
import { EntityMetadataType } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/serverProxy';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

const groupByToEntityTypeMap: Record<string, EntityMetadataType> = {
  subCategoryId: 'subcategory',
  categoryId: 'category'
};

const useSentimentByTaxonomyData = (taxonomyType: 'category' | 'subcategory') => {
  const { startWeek, endWeek } = useMainTimePeriod();
  const theme = useStacklineTheme();

  const groupByFieldName = taxonomyType === 'category' ? 'categoryId' : 'subCategoryId';

  const negativeRequestBuilder = useBaseMetricRequestBuilder({
    requestId: `weeklySentimentByTaxonomy-stackedColumn-negative`,
    indexName: BEACON_INDEX_NAMES.REVIEWS_METRICS,
    groupByFieldName,
    startWeekId: startWeek,
    endWeekId: endWeek,
    fields: [{ name: 'reviewSentiment' }],
    buildAggregationBuilder: (agg) => agg.addConditionTermFilter('reviewSentiment', ['-1'])
  });

  const neutralRequestBuilder = useBaseMetricRequestBuilder({
    requestId: `weeklySentimentByTaxonomy-stackedColumn-neutral`,
    indexName: BEACON_INDEX_NAMES.REVIEWS_METRICS,
    groupByFieldName,
    startWeekId: startWeek,
    endWeekId: endWeek,
    fields: [{ name: 'reviewSentiment' }],
    buildAggregationBuilder: (agg) => agg.addConditionTermFilter('reviewSentiment', ['0'])
  });

  const positiveRequestBuilder = useBaseMetricRequestBuilder({
    requestId: `weeklySentimentByTaxonomy-stackedColumn-positive`,
    indexName: BEACON_INDEX_NAMES.REVIEWS_METRICS,
    groupByFieldName,
    startWeekId: startWeek,
    endWeekId: endWeek,
    fields: [{ name: 'reviewSentiment' }],
    buildAggregationBuilder: (agg) => agg.addConditionTermFilter('reviewSentiment', ['1'])
  });

  negativeRequestBuilder.addConditionTermFilter('reviewSentiment', 'must', ['-1']);
  neutralRequestBuilder.addConditionTermFilter('reviewSentiment', 'must', ['0']);
  positiveRequestBuilder.addConditionTermFilter('reviewSentiment', 'must', ['1']);

  const requests = [negativeRequestBuilder.build(), neutralRequestBuilder.build(), positiveRequestBuilder.build()];

  const { data: response, isLoading } = useGenericAdvancedSearch({
    requestId: 'sentimentByTaxonomy-stackedColumn',
    requestBody: requests,
    queryKeys: [requests]
  });

  const data = useMemo(() => {
    if (!isLoading) {
      try {
        const negativeRawData = response?.data?.[0]?.aggregations?.[`by_${groupByFieldName}`];
        const neutralRawData = response?.data?.[1]?.aggregations?.[`by_${groupByFieldName}`];
        const positiveRawData = response?.data?.[2]?.aggregations?.[`by_${groupByFieldName}`];

        const neutralDataSet = neutralRawData.map((neutralDatum) => {
          return {
            name: neutralDatum.additionalMetaData.name,
            y: neutralDatum.value,
            color: theme.colors.accentGold,
            groupBy: groupByFieldName,
            fieldId: neutralDatum.fieldId
          };
        });

        const negativeDataDataSet = negativeRawData.map((negativeDatum) => {
          return {
            name: negativeDatum.additionalMetaData.name,
            y: negativeDatum.value,
            color: theme.colors.accentMagenta,
            fieldId: negativeDatum.fieldId,
            groupBy: groupByFieldName
          };
        });

        const positiveDataSet = positiveRawData.map((positiveDatum) => {
          return {
            name: positiveDatum.additionalMetaData.name,
            y: positiveDatum.value,
            color: theme.colors.accentPine,
            fieldId: positiveDatum.fieldId,
            groupBy: groupByFieldName
          };
        });

        return [
          { name: 'Negative', data: negativeDataDataSet, color: theme.colors.accentMagenta },
          { name: 'Neutral', data: neutralDataSet, color: theme.colors.accentGold },
          { name: 'Positive', data: positiveDataSet, color: theme.colors.accentPine }
        ];
      } catch (error) {
        console.error('Error parsing sentiment by taxonomy data:', error);
        return [
          { name: 'Negative', data: [], color: theme.colors.accentMagenta },
          { name: 'Neutral', data: [], color: theme.colors.accentGold },
          { name: 'Positive', data: [], color: theme.colors.accentPine }
        ];
      }
    } else {
      return [
        { name: 'Negative', data: [], color: theme.colors.accentMagenta },
        { name: 'Neutral', data: [], color: theme.colors.accentGold },
        { name: 'Positive', data: [], color: theme.colors.accentPine }
      ];
    }
  }, [
    isLoading,
    response,
    groupByFieldName,
    theme.colors.accentMagenta,
    theme.colors.accentGold,
    theme.colors.accentPine
  ]);

  return { data, isLoading };
};

const SentimentByTaxonomyStackedColumnChart = ({ taxonomyType }: { taxonomyType: 'category' | 'subcategory' }) => {
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();
  const { searchParams, additionalParams } = useAppSelector((state) => state.app.queryParams);

  const { data: chartSeries, isLoading } = useSentimentByTaxonomyData(taxonomyType);
  const groupByFieldName = taxonomyType === 'category' ? 'categoryId' : 'subCategoryId';

  return (
    <BeaconChartWithLegend
      convertSeriesToDelimitedData={async () => {
        saveStandardStackedBarChartAsDelimitedData({
          chartSeries,
          options: {
            headers: [`${_capitalize(taxonomyType)}`, 'Positive', 'Neutral', 'Negative']
          }
        });
      }}
      loading={isLoading}
      loadingComponent={<SplineChartLoading width={900} />}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the review sentiment for ${entityPhrase} `}
            {taxonomyText}{' '}
            {`in each ${taxonomyType} from reviews written during the
            ${mainTimePeriodShortDisplayName} period`}
            .
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      subtitleSX={{ marginTop: '8px' }}
      includeSummaryInfo={false}
      customLegends={
        <LegendWithMultipleElement
          multipleLegendsProps={[
            { displayName: 'Positive', barColor: 'accentPine' },
            { displayName: 'Neutral', barColor: 'accentGold' },
            { displayName: 'Negative', barColor: 'accentMagenta' }
          ]}
        />
      }
      title={`Sentiment by ${_capitalize(taxonomyType)}`}
    >
      <StackedTopEntityChart
        chartSeries={[
          {
            type: 'column',
            data: chartSeries,
            metricType: METRICTYPE.VOLUME,
            groupBy: groupByFieldName
          }
        ]}
        tooltipCategoryTitle="REVIEW VOLUME"
        xAxisFormatter={(value: string) => {
          const categoryMap = [];
          chartSeries.forEach((datum) => datum.data.forEach((categoryDatum) => categoryMap.push(categoryDatum)));
          const selectedLabelData = categoryMap?.find((seriesDatum) => seriesDatum.name === value) ?? {};
          return ReactDOMServer.renderToStaticMarkup(
            <div style={{ position: 'relative' }}>
              <XAxisLabel
                name={value}
                link={`/${groupByToEntityTypeMap[groupByFieldName]}/${selectedLabelData.fieldId}${searchParams}${additionalParams}`}
              />
            </div>
          );
        }}
      />
    </BeaconChartWithLegend>
  );
};

export default SentimentByTaxonomyStackedColumnChart;
